import JSZip from "jszip";
import { DataRequestAttachment } from "../hooks/useDataRequestAttachments";
import { PersonalIdentifiersData } from "../components/dataRequest/stayCompliant/DataRequestProcessingContext";

/**
 * Creates a text file containing the form data in a readable format
 */
function createFormDataFile(formData: PersonalIdentifiersData): string {
  // Handle both formats: name field OR firstName/lastName fields
  let firstName = "";
  let lastName = "";

  // Check if formData has a 'name' field (legacy format from RequestDetailPage)
  if ((formData as any).name) {
    const fullName = String((formData as any).name).trim();
    // Try to split the name into first and last name
    const nameParts = fullName.split(/\s+/);
    if (nameParts.length > 0) {
      firstName = nameParts[0];
      lastName = nameParts.slice(1).join(" ");
    }
  } else {
    // Use firstName and lastName fields
    firstName = (formData.firstName || "").trim();
    lastName = (formData.lastName || "").trim();
  }

  const fullName = firstName || lastName ? `${firstName} ${lastName}`.trim() : "N/A";

  const lines = [
    "CONSUMER PERSONAL INFORMATION",
    "=".repeat(50),
    "",
    `Name: ${fullName}`,
    `Email: ${formData.email || "N/A"}`,
  ];

  // Use subjectFreePhone if available, otherwise use phone
  const phoneToDisplay = formData.subjectFreePhone || formData.phone;
  if (phoneToDisplay) {
    lines.push(`Phone Number: ${phoneToDisplay}`);
  }

  // Use subjectFreeTextAddress if available for address
  if (formData.subjectFreeTextAddress) {
    lines.push(`Postal Address: ${formData.subjectFreeTextAddress}`);
  }

  if (formData.onlineIdentifiers) {
    lines.push(`Online Identifiers: ${formData.onlineIdentifiers}`);
  }

  // Add custom fields if they exist (directly after online identifiers)
  if (formData.customFields && formData.customFields.length > 0) {
    formData.customFields.forEach((field) => {
      lines.push(`${field.label}: ${field.value}`);
    });
  }

  lines.push("");
  lines.push("=".repeat(50));
  lines.push(`Generated: ${new Date().toLocaleString()}`);

  return lines.join("\n");
}

/**
 * Downloads a file from a URL and returns it as a Blob
 */
async function downloadFileAsBlob(url: string): Promise<Blob> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to download file from ${url}`);
  }
  return response.blob();
}

/**
 * Creates a zip file containing the form data and all uploaded attachments
 * @param requestorName - The name of the requestor (used for zip filename)
 * @param formData - The personal identifiers form data
 * @param attachments - Array of uploaded attachments
 * @returns A File object representing the zip file
 */
export async function createDataRequestZip(
  requestorName: string,
  formData: PersonalIdentifiersData,
  attachments: DataRequestAttachment[],
): Promise<File> {
  const zip = new JSZip();

  // Add the form data as a text file
  const formDataContent = createFormDataFile(formData);
  zip.file("consumer_information.txt", formDataContent);

  // Add all attachments
  const uploadedFiles = attachments.filter((att) => att.status === "UPLOADED");

  for (const attachment of uploadedFiles) {
    try {
      const blob = await downloadFileAsBlob(attachment.url);
      zip.file(attachment.filename, blob);
    } catch (error) {
      console.error(`Error adding attachment ${attachment.filename} to zip:`, error);
      // Continue with other files even if one fails
    }
  }

  // Generate the zip file
  const zipBlob = await zip.generateAsync({ type: "blob" });

  // Create a safe filename from the requestor name
  const safeFilename = requestorName
    .toLowerCase()
    .replace(/\s+/g, "")
    .replace(/[^a-z0-9]/g, "");

  const filename = `${safeFilename}.zip`;

  // Convert Blob to File
  const zipFile = new File([zipBlob], filename, { type: "application/zip" });

  return zipFile;
}

/**
 * Counts the total number of files that will be in the zip
 * @param attachments - Array of uploaded attachments
 * @returns Total count (includes form data file + attachments)
 */
export function getZipFileCount(attachments: DataRequestAttachment[]): number {
  const uploadedFiles = attachments.filter((att) => att.status === "UPLOADED");
  return uploadedFiles.length + 1; // +1 for the form data file
}
